import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Empty, Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import { getErrorMessage } from '~/app/utils/getErrorMessage';
import { useAssetCustomFieldColumns } from '~/app/components/asset-custom-field/useAssetCustomFieldColumns';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type AssetCustomFieldTableCardProps = {
  fields: FindAssetCustomFieldResponseDto[];
  isLoading: boolean;
  error: unknown;
  onCreate: () => void;
  onEdit: (field: FindAssetCustomFieldResponseDto) => void;
  onDelete: (field: FindAssetCustomFieldResponseDto) => void;
};

export function AssetCustomFieldTableCard({
  fields,
  isLoading,
  error,
  onCreate,
  onEdit,
  onDelete,
}: AssetCustomFieldTableCardProps) {
  const { t } = useTranslation('assetCustomFields');

  const columns = useAssetCustomFieldColumns({ t, onEdit, onDelete });

  // ── Error state ──────────────────────────────────────────
  if (error) {
    return (
      <Card
        title={t('sections.fields', 'Custom Fields')}
        className="app-asset-custom-field-card"
      >
        <Alert
          type="error"
          showIcon
          title={getErrorMessage(
            error,
            t,
            t('messages.fieldsLoadFailed', 'Failed to load custom fields'),
          )}
        />
      </Card>
    );
  }

  // ── Empty state ──────────────────────────────────────────
  if (!isLoading && fields.length === 0) {
    return (
      <Card
        title={t('sections.fields', 'Custom Fields')}
        className="app-asset-custom-field-card"
      >
        <Flex justify="center" className="app-asset-custom-field-empty">
          <Empty
            description={t('empty.fields', 'No custom fields yet')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreate}
              className="app-asset-custom-field-add-btn"
            >
              {t('actions.createFirstField', 'Create first field')}
            </Button>
          </Empty>
        </Flex>
      </Card>
    );
  }

  // ── Table state ──────────────────────────────────────────
  return (
    <Card
      title={t('sections.fields', 'Custom Fields')}
      className="app-asset-custom-field-card"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onCreate}
          className="app-asset-custom-field-add-btn"
        >
          {t('actions.addField', 'Add Field')}
        </Button>
      }
    >
      <EvimoTable<FindAssetCustomFieldResponseDto>
        className="app-asset-custom-field-table"
        dataSource={fields}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={isLoading}
        size="middle"
      />
    </Card>
  );
}
